<template>
  <MeasurementCard
    :title="title"
    :info="info"
    :value="
      valueNumber !== undefined
        ? value?.type === 'gauge'
          ? {
              type: 'gauge',
              number: valueNumber,
              min: value?.min,
              max: value?.max,
              format: value?.format,
              isLoading: valueDataSource?.isLoading ?? false
            }
          : {
              type: 'number',
              number: valueNumber,
              format: value?.format,
              textType: value?.textType,
              isLoading: valueDataSource?.isLoading ?? false
            }
        : {
            type: 'string',
            string: '-',
            isLoading: valueDataSource?.isLoading ?? false,
            textType: value && 'textType' in value ? value.textType : undefined
          }
    "
    :color="color"
    :colorScale="colorScale"
    :colorLegend="colorLegend"
    :coloredEntities="{
      ...coloredEntities,
      colorings
    }"
    :initialShowOnMap="initialShowOnMap"
  >
    <template v-if="table && table.headers.length && table.rows.length > 0" #details>
      <TableDataSource :table="table" />
    </template>
  </MeasurementCard>
</template>
<script lang="ts" setup>
import MeasurementCard, { Coloring } from './MeasurementCard.vue'
import { computed, toRefs } from 'vue'
import { FormatOptions } from '../formatter'
import { ColorScaleConfig } from '@/utils/colorScale'
import { EntityLevel } from '@/composables/useEntityLevel'
import TableDataSource, { Table } from './TableDataSource.vue'
import { useDataSource } from '@/composables/useDataSources'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { TextType } from '@/design-system/atoms/TextBase.vue'
import { useComponentFilter } from '../measurements/useMeasurements'
import { Feature } from '@/pinia/feature'

export type ValueType = 'number' | 'gauge'

export type Value<Type extends ValueType = ValueType> = Type extends 'number'
  ? {
      type: 'number'
      dataSource: string
      textType?: TextType
      format?: FormatOptions
    }
  : {
      type: 'gauge'
      dataSource: string
      min?: number
      max?: number
      format?: FormatOptions
    }

export type ColoredEntities = {
  dataSource: string
  enabledEntityLevels?: EntityLevel[]
}

export type ColorLegend = {
  width?: number
  height?: number
  padding?: number
  hideMean?: boolean
  format?: FormatOptions
  labelStart?: LocaleString
  labelEnd?: LocaleString
}

export type TableConfig = {
  dataSource: string
  sort?: { col: number; direction: 'asc' | 'desc'; field: string }
  headers: LocaleString[]
}

type Props = {
  title: string
  info?: string
  value?: Value
  colorScale?: ColorScaleConfig
  color?: string
  coloredEntities?: ColoredEntities
  colorLegend?: ColorLegend
  initialShowOnMap?: boolean
  table?: TableConfig
  featureName: string
  children?: Feature[]
}

const props = withDefaults(defineProps<Props>(), {
  info: undefined,
  value: undefined,
  color: undefined,
  coloredEntities: undefined,
  initialShowOnMap: false,
  table: undefined,
  colorScale: undefined,
  children: () => [],
  colorLegend: () => ({
    height: 60,
    padding: 20,
    hideMean: false
  }),
  descriptionCaption: () => ({ sv: 'Detaljer', en: 'Details' })
})

const { value: valueConf, coloredEntities, table: tableConfig } = toRefs(props)

useComponentFilter(props.featureName, {})

const valueDataSource = useDataSource<number>(valueConf.value?.dataSource)
const tableDS = useDataSource<Table['rows']>(tableConfig.value?.dataSource)
const coloringDataSource = useDataSource<Coloring[]>(coloredEntities.value?.dataSource)

const valueNumber = computed<number | undefined>(() => valueDataSource.value?.data)
const tableRows = computed<Table['rows'] | undefined>(() => tableDS.value?.data)
const colorings = computed<Coloring[] | undefined>(() => coloringDataSource.value?.data)

const table = computed<Table>(() => ({
  sort: {
    col: tableConfig.value?.sort?.col ?? 0,
    direction: tableConfig.value?.sort?.direction ?? 'asc',
    field: tableConfig.value?.sort?.field ?? 'text'
  },
  headers: tableConfig.value?.headers ?? [],
  rows: tableRows.value ?? []
}))
</script>
